import { studentApi } from './studentApi';
import { API_ENDPOINTS } from './api';
import type { Course, CourseType, CourseStatus } from '../types';

export interface CourseSearchParams {
  courseType?: CourseType;
  status?: CourseStatus;
  searchTerm?: string;
  difficultyLevel?: string;
}

export interface AvailableCoursesParams {
  courseType?: string;
  q?: string;
}

export interface CourseService {
  getAllCourses: () => Promise<Course[]>;
  getCoursesByType: (courseType: CourseType) => Promise<Course[]>;
  getAvailableCourses: (params?: AvailableCoursesParams) => Promise<Course[]>;
  searchCourses: (params: CourseSearchParams) => Promise<Course[]>;
  getCourseById: (id: number) => Promise<Course>;
}

class CourseServiceImpl implements CourseService {
  
  async getAllCourses(): Promise<Course[]> {
    try {
      const response = await studentApi.get(API_ENDPOINTS.COURSES.LIST);
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching courses:', error);
      return [];
    }
  }

  async getCoursesByType(courseType: CourseType): Promise<Course[]> {
    try {
      const response = await studentApi.get(`${API_ENDPOINTS.COURSES.LIST}/type/${courseType}`);
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching courses by type:', error);
      return [];
    }
  }

  async getAvailableCourses(params?: AvailableCoursesParams): Promise<Course[]> {
    try {
      const search = new URLSearchParams();
      if (params?.courseType) {
        search.set('courseType', params.courseType);
      }
      if (params?.q) {
        search.set('q', params.q);
      }
      const qs = search.toString();
      const url = qs ? `${API_ENDPOINTS.COURSES.AVAILABLE}?${qs}` : API_ENDPOINTS.COURSES.AVAILABLE;
      const response = await studentApi.get(url);
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching available courses:', error);
      return [];
    }
  }

  async searchCourses(params: CourseSearchParams): Promise<Course[]> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.courseType) queryParams.append('courseType', params.courseType);
      if (params.status) queryParams.append('status', params.status);
      if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
      if (params.difficultyLevel) queryParams.append('difficultyLevel', params.difficultyLevel);

      const response = await studentApi.get(`${API_ENDPOINTS.COURSES.SEARCH}?${queryParams.toString()}`);
      return response.data.data || [];
    } catch (error) {
      console.error('Error searching courses:', error);
      return [];
    }
  }

  async getCourseById(id: number): Promise<Course> {
    try {
      const response = await studentApi.get(`${API_ENDPOINTS.COURSES.DETAILS}/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching course by ID:', error);
      throw new Error('Course not found');
    }
  }
}

export const courseService = new CourseServiceImpl();
export default courseService;
